import {
  call, put, select, takeEvery, takeLatest,
} from 'redux-saga/effects';
import { api } from '@/utils/api';
import { AdvancedHostingActionTypes } from './advancedHosting.types';
import advancedHostingActions from './advancedHosting.actions';
import logger from '@/utils/logger';
import {
  ADVANCED_HOSTING_URL,
  PRODUCT_VPSOCI_STATUS_URL,
} from '@/config/api';

/**
 * Constrói query string a partir dos parâmetros
 * @param {Object} params - { search, order, sort, page }
 * @returns {string} Query string formatada (ex: "?search=test&page=2")
 */
function buildQueryString(params = {}) {
  const queryParams = new URLSearchParams();

  if (params.search) {
    queryParams.append('search', params.search);
  }

  if (params.order) {
    queryParams.append('order', params.order);
  }

  if (params.sort) {
    queryParams.append('sort', params.sort);
  }

  if (params.page && params.page > 1) {
    queryParams.append('page', params.page);
  }

  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : '';
}

function* fetchListServers(action) {
  const { token, jwt: jwtToken } = yield select(state => state.auth);

  yield put(advancedHostingActions.list.loading(true));
  yield put(advancedHostingActions.list.setLastListParams(action.payload || {}));

  const queryString = buildQueryString(action.payload);

  const url = `${ADVANCED_HOSTING_URL}${queryString}`;

  const request = {
    url,
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Service-Token': jwtToken,
    },
  };

  try {
    const response = yield call(api.whmcs.get, request);
    const {
      data: servers,
      currentPage,
      lastPage,
      perPage,
      total,
    } = response?.data;

    yield put(advancedHostingActions.list.success({
      servers,
      pagination: {
        currentPage,
        lastPage,
        perPage,
        total,
      },
    }));
  } catch (error) {
    yield put(advancedHostingActions.list.failure(error));
    logger.error('Error sagas advancedHosting - method fetchListServers', error);
  } finally {
    yield put(advancedHostingActions.list.loading(false));
  }
}

function* updateServer(action) {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const { serverId, nickName, isFavorite } = action.payload;

  yield put(advancedHostingActions.updateServer.loading(true));

  const data = {};

  if (nickName !== undefined) {
    data.nickName = nickName;
  }

  if (isFavorite !== undefined) {
    data.isFavorite = isFavorite;
  }

  const request = {
    url: `${ADVANCED_HOSTING_URL}/${serverId}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Service-Token': jwtToken,
    },
    data,
  };

  try {
    const response = yield call(api.whmcs.patch, request);
    yield put(advancedHostingActions.updateServer.success(response.data));

    const lastListParams = yield select(state => state.advancedHosting?.list?.lastListParams);
    yield put(advancedHostingActions.list.request(lastListParams));
  } catch (error) {
    yield put(advancedHostingActions.updateServer.failure(error));
    logger.error('Error sagas advancedHosting - method updateServer', error);
  } finally {
    yield put(advancedHostingActions.updateServer.loading(false));
  }
}

function* fetchServerStatus(action) {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const { serverId } = action.payload;

  yield put(advancedHostingActions.serverStatus.loading({ serverId, loading: true }));

  const request = {
    url: PRODUCT_VPSOCI_STATUS_URL(serverId),
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Service-Token': jwtToken,
    },
  };

  try {
    const response = yield call(api.whmcs.get, request);
    const data = response.data && response.data.data;

    yield put(advancedHostingActions.serverStatus.success({
      serverId,
      status: data?.status ?? null,
    }));
  } catch (error) {
    yield put(advancedHostingActions.serverStatus.failure({
      serverId,
      error,
    }));
    logger.error('Error sagas advancedHosting - method fetchServerStatus', error);
  }
}

function* sagas() {
  yield takeLatest(AdvancedHostingActionTypes.ADVANCED_HOSTING_LIST_REQUEST, fetchListServers);
  yield takeLatest(AdvancedHostingActionTypes.ADVANCED_HOSTING_UPDATE_SERVER_REQUEST, updateServer);
  yield takeEvery(AdvancedHostingActionTypes.ADVANCED_HOSTING_SERVER_STATUS_REQUEST, fetchServerStatus);
}

export default sagas;
